import { Rs232Controller } from './Rs232Controller';

/* global BSSerialPort */

/**
 * BrightSign Rs232Controller
 * @class
 * @augments Rs232Controller
 * @param {string} comPort - the name of the COM port to open
 * @param {ComSettings} comSettings - settings for the com port
 */
function BrightSignRs232Controller(comPort, comSettings) {
    Rs232Controller.call(this, comPort, comSettings);

    // convert to formatting brightsign expects
    if (this.stopBits === 'One') {
        this.stopBits = 1;
    }
    if (this.stopBits === 'Two') {
        this.stopBits = 2;
    }
    this.parity = this.parity.toLowerCase();

    this.open();
}

BrightSignRs232Controller.prototype = Object.create(Rs232Controller.prototype);
BrightSignRs232Controller.prototype.constructor = BrightSignRs232Controller;

/**
 * opens a connection to the COM port using internal settings
 */
BrightSignRs232Controller.prototype.open = function () {
    this.readBuffer = [];

    if (this.bsSerialPort == null) {
        this.bsSerialPort = new BSSerialPort(Number(this.comPort));

        this.bsSerialPort.SetBaudRate(this.baudRate);
        this.bsSerialPort.SetDataBits(this.dataBits);
        this.bsSerialPort.SetStopBits(this.stopBits);
        this.bsSerialPort.SetParity(this.parity);
        this.bsSerialPort.SetFlowControl(this.rtsEnabled);
        this.bsSerialPort.SetLineEnding(this.newLine);

        // brightsign does not support timeouts, so will need to impliment
        // ourselves

        // tell it to fire the events when the BS unit recieves data
        this.bsSerialPort.SetGenerateByteEvent(true);

        this.bsSerialPort.onserialbyte = function (e) {
            this.readBuffer.push(e.sbyte);
        }.bind(this);
    }
};

/**
 * closes a connection to the COM port
 */
BrightSignRs232Controller.prototype.close = function () {
    this.bsSerialPort.Close();
    this.bsSerialPort = null;
};

/**
 * writes an array of bytes to the COM port
 * @param {Uint8Array} data - the data to write to the port
 */
BrightSignRs232Controller.prototype.write = function (data) {
    // clear out the read buffer as we do not want previous communication to
    // show as if it came from the current command
    this.readBuffer = [];

    //note: REACH.ComPort.Write accepts message as string, thus null bytes will
    // break! We will use
    //      WriteHexEncodedString instead which will be safe (but slower)
    this.bsSerialPort.SendBytes(String.fromCharCode.apply(null, data));
};

/**
 * writes an array of bytes to the COM port with an newLine character
 * @param {Uint8Array} data - the data to write to the port
 */
BrightSignRs232Controller.prototype.writeLine = function (data) {
    // clear out the read buffer as we do not want previous communication to
    // show as if it came from the current command
    this.readBuffer = [];

    this.bsSerialPort.SendBytes(String.fromCharCode.apply(null, data) + this.newLine);
};

/**
 * performs the read operation for the read function
 * @returns {string}
 */
BrightSignRs232Controller.prototype.readPerformRead = function (numBytes) {
    var result = this.readBuffer.slice(0, numBytes);
    // remove items from read buffer because they are now considdered
    // "read"
    this.readBuffer = this.readBuffer.slice(numBytes);
    return String.fromCharCode.apply(null, result);
};

/**
 * reads numBytes bytes from the COM port
 * @param {number} numBytes - the number of bytes to read from the COM port
 * @returns {jQuery.Promise<int[]>}
 *      Success `jQuery.Promise[int[]]`  <br />
 *      Failure `jQuery.Promise`
 */
BrightSignRs232Controller.prototype.read = function (numBytes) {
    return new Promise(
        function (resolve, reject) {
            if (this.readBuffer.length >= numBytes) {
                resolve(this.readPerformRead(numBytes));
            } else {
                // start a worker to watch until the desired amount of bytes are
                // accumulated in the read buffer
                var iterationsLeft = this.readTimeout * 10; // iterating at 10 times
                // per second
                var readerWorker = setInterval(
                    function () {
                        iterationsLeft--;

                        if (iterationsLeft < 0) {
                            clearInterval(readerWorker);
                            reject(new Error('Read Timeout reached on COM port: ' + this.comPort));
                            return;
                        }

                        if (this.readBuffer.length >= numBytes) {
                            resolve(this.readPerformRead(numBytes));
                        }
                    }.bind(this),
                    100
                );
            }
        }.bind(this)
    );
};

/**
 * performs the read operation for the readLine function
 * @returns {string}
 */
BrightSignRs232Controller.prototype.readLinePerformRead = function () {
    // read until the first newline character
    var numBytes = this.readBuffer.indexOf(this.newLine.charCodeAt(0)) + 1;
    var result = this.readBuffer.slice(0, numBytes);
    // remove items from read buffer because they are now considered
    // "read"
    this.readBuffer = this.readBuffer.slice(numBytes);
    return String.fromCharCode.apply(null, result);
};

/**
 * reads bytes from the COM port until newLine is encountered
 * @returns {jQuery.Promise<int[]>}
 *      Success `jQuery.Promise[int[]]`  <br />
 *      Failure `jQuery.Promise`
 */
BrightSignRs232Controller.prototype.readLine = function () {
    return new Promise(
        function (resolve, reject) {
            if (this.readBuffer.indexOf(this.newLine.charCodeAt(0)) > -1) {
                resolve(this.readLinePerformRead());
            } else {
                // start a worker to watch until the desired amount of bytes are
                // accumulated in the read buffer
                var iterationsLeft = this.readTimeout * 10; // iterating at 10 times
                // per second
                var readerWorker = setInterval(
                    function () {
                        iterationsLeft--;

                        if (iterationsLeft < 0) {
                            clearInterval(readerWorker);
                            reject(new Error('Read Timeout reached on COM port: ' + this.comPort));
                            return;
                        }

                        if (this.readBuffer.indexOf(this.newLine.charCodeAt(0)) > -1) {
                            resolve(this.readLinePerformRead());
                        }
                    }.bind(this),
                    100
                );
            }
        }.bind(this)
    );
};

export { BrightSignRs232Controller };
